import { useCallback, useEffect, useMemo, useState } from 'react';
import { useUserInfo } from './useAuth';

// 설치 후 3일까지만 온보딩 가이드 노출 (체크리스트 + DailyFeatureBanner 둘 다 적용).
// 이전: 30 → 1 → (현재) 3. 1일은 너무 짧다는 사용자 피드백, 30일은 노이즈.
const NEW_USER_DAYS = 3;

/**
 * 신규 유저 온보딩 상태 (가입일 1일 이내 판정).
 *
 * 서버에서 createdAt 을 내려주지 않는 현 SSO 구조를 감안해
 * "이 기기에서 이 userNo 를 처음 본 날짜" 를 가입일 프록시로 사용.
 * localStorage: `dokocrew_first_seen_{userNo}`
 *
 * dismiss / complete 플래그도 동일 규칙으로 userNo 별 격리.
 */
export const useOnboardingState = () => {
    const { userNo, isLoaded } = useUserInfo();
    const [firstSeen, setFirstSeen] = useState<number | null>(null);
    const [now, setNow] = useState<number>(Date.now());
    // dismiss / complete 플래그 변경을 감지하기 위한 tick
    const [, setTick] = useState(0);

    useEffect(() => {
        if (!isLoaded || !userNo) return;
        const key = `dokocrew_first_seen_${userNo}`;
        const saved = localStorage.getItem(key);
        if (saved) {
            const n = Number(saved);
            if (!isNaN(n)) setFirstSeen(n);
            else {
                const nowTs = Date.now();
                localStorage.setItem(key, String(nowTs));
                setFirstSeen(nowTs);
            }
        } else {
            const nowTs = Date.now();
            localStorage.setItem(key, String(nowTs));
            setFirstSeen(nowTs);
        }
        setNow(Date.now());
    }, [userNo, isLoaded]);

    const daysSinceFirstSeen = useMemo(() => {
        if (!firstSeen) return 0;
        return Math.floor((now - firstSeen) / (1000 * 60 * 60 * 24));
    }, [firstSeen, now]);

    const isNewUser = useMemo(
        () => firstSeen != null && daysSinceFirstSeen <= NEW_USER_DAYS,
        [firstSeen, daysSinceFirstSeen]
    );

    const flagKey = useCallback((k: string) => `dokocrew_onboard_${userNo}_${k}`, [userNo]);

    const isDismissed = useCallback((k: string) => {
        if (!userNo) return false;
        return localStorage.getItem(flagKey(k)) === 'dismissed'
            || localStorage.getItem(flagKey(k)) === 'completed';
    }, [userNo, flagKey]);

    const isCompleted = useCallback((k: string) => {
        if (!userNo) return false;
        return localStorage.getItem(flagKey(k)) === 'completed';
    }, [userNo, flagKey]);

    const dismiss = useCallback((k: string) => {
        if (!userNo) return;
        localStorage.setItem(flagKey(k), 'dismissed');
        setTick(t => t + 1);
    }, [userNo, flagKey]);

    const markCompleted = useCallback((k: string) => {
        if (!userNo) return;
        localStorage.setItem(flagKey(k), 'completed');
        setTick(t => t + 1);
    }, [userNo, flagKey]);

    const reset = useCallback((k?: string) => {
        if (!userNo) return;
        if (k) {
            localStorage.removeItem(flagKey(k));
        } else {
            Object.keys(localStorage).forEach(key => {
                if (key.startsWith(`dokocrew_onboard_${userNo}_`)) localStorage.removeItem(key);
            });
        }
        setTick(t => t + 1);
    }, [userNo, flagKey]);

    return {
        isNewUser,
        daysSinceFirstSeen,
        firstSeen,
        isDismissed,
        isCompleted,
        dismiss,
        markCompleted,
        reset,
        ready: firstSeen != null,
        userNo,
    };
};
